import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { PdaPath } from '../../../enums/PdaNavigatePathEnum';
import { Header } from '../components/Header';
import { t } from '../../../i18n';
import { TmsPageState } from '../types/tms';
import {
  GET_TMS_UNLOADING_BAG_DETAIL,
  GetTmsUnloadingBagDetailResponse,
  TmsUnloadingBagDetailItem,
} from '../../../graphql/queries/tmsTaskWarehouseOperationQueries';

interface BagDetailPageState extends TmsPageState {
  detailType: number; // 1=应卸 2=实卸 3=未卸
  parentBackRoute?: string; // 卸车页原来的 backRoute（任务列表路径）
}

const DETAIL_TYPE_CONFIG: Record<number, { title: string; totalBagLabel: string; totalParcelLabel: string }> = {
  1: {
    title: 'pda.pages.unloadingDetail.expectedBagDetailTitle',
    totalBagLabel: 'pda.pages.unloadingDetail.expectedBagCount',
    totalParcelLabel: 'pda.pages.unloadingDetail.totalParcelCount',
  },
  2: {
    title: 'pda.pages.unloadingDetail.actualBagDetailTitle',
    totalBagLabel: 'pda.pages.unloadingDetail.actualBagCount',
    totalParcelLabel: 'pda.pages.unloadingDetail.totalParcelCount',
  },
  3: {
    title: 'pda.pages.unloadingDetail.unloadedBagDetailTitle',
    totalBagLabel: 'pda.pages.unloadingDetail.unloadedBagCount',
    totalParcelLabel: 'pda.pages.unloadingDetail.totalParcelCount',
  },
};

const DETAIL_TYPE_FALLBACK: Record<number, { title: string; totalBagLabel: string }> = {
  1: { title: '应卸明细', totalBagLabel: '应卸' },
  2: { title: '实卸明细', totalBagLabel: '实卸' },
  3: { title: '未卸明细', totalBagLabel: '未卸' },
};

export default function TmsUnloadingBagDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as BagDetailPageState) || null;
  const { backRoute, detailType, parentBackRoute, ...taskData } = state ?? {} as BagDetailPageState;

  const taskId = taskData?.taskId ? parseInt(taskData.taskId) : NaN;
  const taskWayPointId = taskData?.taskWaypointId ? parseInt(taskData.taskWaypointId) : NaN;
  const isValid = !!(taskData && !isNaN(taskId) && !isNaN(taskWayPointId) && detailType);

  const config = DETAIL_TYPE_CONFIG[detailType] ?? DETAIL_TYPE_CONFIG[1];
  const fallback = DETAIL_TYPE_FALLBACK[detailType] ?? { title: '明细', totalBagLabel: '' };

  const title = t(config.title) || fallback.title;
  const totalBagLabel = t(config.totalBagLabel) || fallback.totalBagLabel;
  const totalParcelLabel = t(config.totalParcelLabel) || '运单总数';

  const { data, loading } = useQuery<GetTmsUnloadingBagDetailResponse>(GET_TMS_UNLOADING_BAG_DETAIL, {
    variables: { taskId, taskWayPointId, detailType },
    skip: !isValid,
  });

  if (!isValid) return null;

  const detail = data?.getTmsUnloadingBagDetail;
  const items = detail?.items ?? [];

  return (
    <div className="w-full h-full flex flex-col bg-gray-50">
      <div className="flex flex-col gap-2 p-2 flex-1 overflow-y-auto">
        <Header
          title={title}
          onBack={() => navigate(PdaPath.PDA_TMS_UNLOADING, { state: { backRoute: parentBackRoute, ...taskData } })}
        />

        {/* 汇总信息 */}
        <div className="bg-white rounded-lg p-3 shadow-sm flex flex-col gap-1">
          <div className="flex items-baseline gap-1">
            <span className="text-sm font-semibold text-black">
              {totalBagLabel}{t('pda.pages.unloadingDetail.totalBagCount') || '包牌总数'}：
            </span>
            <span className="text-sm font-semibold text-purple-600">{detail?.totalBagCount ?? 0}</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-sm font-semibold text-black">
              {totalBagLabel}{totalParcelLabel}：
            </span>
            <span className="text-sm font-semibold text-purple-600">{detail?.totalParcelCount ?? 0}</span>
          </div>
        </div>

        {/* 明细列表 */}
        <div>
          {/* 表头 */}
          <div className="flex px-3 py-2 border-b border-gray-200">
            <span className="flex-1 text-xs font-semibold text-black">
              {t('pda.pages.unloadingDetail.tableColBagOrTracking') || '包牌号/运单号'}
            </span>
            <span className="w-20 text-xs font-semibold text-black text-center">
              {t('pda.pages.unloadingDetail.tableColDestination') || '目的地中心'}
            </span>
            <span className="w-14 text-xs font-semibold text-black text-center">
              {t('pda.pages.unloadingDetail.tableColParcelQty') || '运单数量'}
            </span>
            <span className="w-12 text-xs font-semibold text-black text-center">
              {t('pda.pages.unloadingDetail.tableColStatus') || '状态'}
            </span>
          </div>

          {/* 数据行 */}
          {loading && (
            <div className="flex items-center justify-center py-8 text-sm text-gray-400">
              {t('pda.common.loading') || 'Loading...'}
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="flex items-center justify-center py-8 text-sm text-gray-400">
              {t('pda.common.noData') || '暂无数据'}
            </div>
          )}
          {items.map((item: TmsUnloadingBagDetailItem, idx: number) => (
            <div
              key={idx}
              className="flex px-3 py-2 items-center border-b border-gray-100"
            >
              <span className="flex-1 text-xs text-gray-800 break-all pr-1">
                {item.displayNumber || '-'}
              </span>
              <span className="w-20 text-xs text-gray-800 text-center">
                {item.destinationSortingCode || '-'}
              </span>
              <span className="w-14 text-xs text-gray-800 text-center">
                {item.parcelQuantity}
              </span>
              <span className={`w-12 text-xs text-center font-medium ${item.unloadingStatus === 1 ? 'text-green-600' : 'text-red-500'}`}>
                {item.unloadingStatus === 1
                  ? (t('pda.pages.unloadingDetail.statusUnloaded') || '已卸车')
                  : (t('pda.pages.unloadingDetail.statusNotUnloaded') || '未卸车')}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
